import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import styles from './stories.module.css';
import storyicon from '../../Assets/icons/icon5green.png';

const STORY_HOVER_ICON =
  'https://cdn.nasrollah.info/web_stories_38dp_000000_FILL0_wght400_GRAD0_opsz40.png';

const BASE_URL = 'https://www.nasrollah.info'; 
const ITEMS_PER_PAGE = 12;

// Replace these with your real utility functions
const englishToPersian = (num) => num;
const formatSlugForURL = (slug) => slug;
const formatDisplayDate = (item) => item.date;

// Normalizes keywords: array of strings OR comma-separated string
const normalizeKeywords = (keywords) => {
  if (Array.isArray(keywords)) {
    return keywords.filter(Boolean).slice(0, 6);
  }

  if (typeof keywords === 'string' && keywords.trim().length > 0) {
    return keywords
      .split(',')
      .map((kw) => kw.trim())
      .filter(Boolean)
      .slice(0, 6);
  }

  return [];
};

// ─── بهبود SEO: تایتل، متاتگ‌ها، Canonical و داده‌های ساختاریافته ───
const applySeo = ({ allStories, activeYear }) => {
  const pageUrl = `${BASE_URL}/stories`;
  const totalStories = allStories.length;
  const yearLabel = activeYear ? englishToPersian(activeYear) : '';

  const pageTitle = `داستان‌ها | بخش داستان و قصه‌ها${yearLabel ? ` - سال ${yearLabel}` : ''}`;
  const pageDesc = `مجموعه داستان‌ها و قصه‌های ویژه${yearLabel ? ` برای سال ${yearLabel}` : ''} — ${Math.min(totalStories, 12)} داستان برتر`;
  const keywordsList = [
    ...new Set(allStories.flatMap((s) => normalizeKeywords(s.keywords))),
  ]
    .slice(0, 10)
    .join('، ');

  // عنوان صفحه
  document.title = pageTitle;

  const setMeta = (name, content) => {
    let el = document.querySelector(`meta[name="${name}"]`);
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute('name', name);
      document.head.appendChild(el);
    }
    el.setAttribute('content', content);
  };

  const setProp = (property, content) => {
    let el = document.querySelector(`meta[property="${property}"]`);
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute('property', property);
      document.head.appendChild(el);
    }
    el.setAttribute('content', content);
  };

  // متاتگ‌های پایه + Open Graph
  setMeta('description', pageDesc);
  setMeta('keywords', keywordsList);
  setProp('og:title', pageTitle);
  setProp('og:description', pageDesc);
  setProp('og:type', 'website');
  setProp('og:url', pageUrl);
  setProp('og:site_name', 'سایت شما');
  setProp('og:locale', 'fa_IR');
  setProp('og:image', STORY_HOVER_ICON);

  // Twitter card
  setMeta('twitter:card', 'summary_large_image');
  setMeta('twitter:title', pageTitle);
  setMeta('twitter:description', pageDesc);
  setMeta('twitter:image', STORY_HOVER_ICON);

  // Canonical → همیشه نقطه ورود اصلی را نشان می‌دهد
  let canonical = document.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    document.head.appendChild(canonical);
  }
  canonical.setAttribute('href', pageUrl);

  // JSON-LD → ساختار ItemList برای سئوی لیست داستان‌ها
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: pageTitle,
    description: pageDesc,
    url: pageUrl,
    inLanguage: 'fa-IR',
    mainEntity: {
      '@type': 'ItemList',
      name: 'لیست داستان‌ها',
      itemListElement: allStories.slice(0, 12).map((story, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: story.title,
        url: `${BASE_URL}/stories/${formatSlugForURL(story.slug)}/${story.id}`,
        datePublished: story.originalDate || story.date,
        dateModified: story.date,
        author: { '@type': 'Person', name: story.author },
        image: story.featured_cover || storyicon,
      })),
    },
  };

  let script = document.querySelector('#stories-jsonld');
  if (!script) {
    script = document.createElement('script');
    script.setAttribute('type', 'application/ld+json');
    script.setAttribute('id', 'stories-jsonld');
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(jsonLd);
};

const StoriesSection = () => {
  const [allStories, setAllStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeYear, setActiveYear] = useState(null);
  const [displayLimit, setDisplayLimit] = useState(ITEMS_PER_PAGE);

  const transformStories = (rawData) => {
    return rawData.map((item) => ({
      id: item.id_post,
      title: item.title_post,
      slug: item.slug_post,
      author: item.author_post,
      date: item.create_time_post,
      originalDate: item.original_time_post,
      status: item.status_post,
      featured_media: item.featured_media_post,
      featured_cover: item.featured_post,
      content: item.html_post,
      category: item.name_category || 'story',
      keywords: item.keywords_post,
      description: item.meta_description_post,
      year: new Date(
        item.original_time_post || item.create_time_post
      ).getFullYear(),
    }));
  };

  useEffect(() => {
    const fetchAllStories = async () => {
      setLoading(true);

      try {
        const response = await axios.get(
          'https://api.nasrollah.info/fa/?function=getLatestStories'
        );

        const rawStories = Array.isArray(response.data)
          ? response.data
          : response.data.stories || [];

        const transformedStories = transformStories(rawStories);

        setAllStories(transformedStories);

        if (transformedStories.length > 0) {
          const years = [
            ...new Set(transformedStories.map((story) => story.year)),
          ].sort((a, b) => b - a);

          setActiveYear(years[0]);
        }
      } catch (error) {
        console.error('Error fetching stories:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllStories();
  }, []);

  // رفرش تگ‌های SEO وقتی داده‌ها یا سال فعال تغییر می‌کند
  useEffect(() => {
    applySeo({ allStories, activeYear });
  }, [allStories, activeYear]);

  const availableYears = useMemo(() => {
    const years = [...new Set(allStories.map((story) => story.year))];
    return years.sort((a, b) => b - a);
  }, [allStories]);

  const filteredStories = useMemo(() => {
    if (!activeYear) {
      return [];
    }

    return allStories.filter((story) => story.year === activeYear);
  }, [allStories, activeYear]);

  const visibleStories = useMemo(() => {
    return filteredStories.slice(0, displayLimit);
  }, [filteredStories, displayLimit]);

  const totalFiltered = filteredStories.length;

  useEffect(() => {
    setDisplayLimit(ITEMS_PER_PAGE);
  }, [activeYear]);

    const handleShowMoreStories = () => {
    setDisplayLimit((previousLimit) => previousLimit + ITEMS_PER_PAGE);
  };

  return (
    <div className={styles.storiesWrapper}>
      {loading && (
        <div className={styles.loadingIndicator}>
          در حال بارگذاری داستان‌ها...
        </div>
      )}

      {!loading && visibleStories.length > 0 && (
        <div className={styles.storiesGrid}>
          {visibleStories.map((story, index) => {
            const keywords = normalizeKeywords(story.keywords);

            return (
              <Link
                key={`${activeYear}-${story.id}-${index}`}
                to={`/stories/${formatSlugForURL(
                  story.slug
                )}/${story.id}`}
                className={styles.storyCard}
                title={story.title}
                aria-label={`مشاهده ${story.title}`}
              >
                <div className={styles.storyCoverContainer}>
                  <img
                    src={story.featured_cover || storyicon}
                    alt={`${story.title} - داستان ${englishToPersian(
                      activeYear
                    )}`}
                    className={styles.storyCover}
                    loading="lazy"
                    onError={(event) => {
                      event.currentTarget.src = storyicon;
                    }}
                  />

                  
                  <div className={styles.storyOverlay}>
  <img
    src={storyicon}
    alt=""
    className={styles.storyIcon}
    loading="lazy"
    aria-hidden="true"
  />
</div>
                </div>

                <div className={styles.storyContent}>
                  <h3 className={styles.storyTitle}>{story.title}</h3>

                  <div className={styles.metastyle}>
                    <div className={styles.storyMeta}>
                      <span className={styles.storyAuthor}>{story.author}</span>

                      <span className={styles.storyMetaDivider}>|</span>

                      <span className={styles.storyDate}>
                        {formatDisplayDate(story)}
                      </span>

                      {keywords.map((kw, index) => (
                        <React.Fragment key={index}>
                          <span className={styles.storyMetaDivider}>|</span>
                          <span className={styles.storyKeywordInline}>{kw}</span>
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {!loading &&
        activeYear &&
        filteredStories.length === 0 &&
        availableYears.length > 0 && (
          <div className={styles.noStoriesMessage}>
            هیچ داستانی برای سال {englishToPersian(activeYear)} موجود نیست
          </div>
        )}

      {!loading && visibleStories.length < totalFiltered && (
        <div className={styles.loadMoreSection}>
          <button
            type="button"
            onClick={handleShowMoreStories}
            className={styles.loadMoreBtn}
          >
            نمایش داستان‌های بیشتر
          </button>

          <div className={styles.storyCount}>
            نمایش {englishToPersian(visibleStories.length)} از{' '}
            {englishToPersian(totalFiltered)} داستان
          </div>
        </div>
      )}
    </div>
  );
};

export default StoriesSection;
